/**
 * NameGate (T006, T014) — shown when a visitor opens a room link without
 * a remembered display name. Presents two paths:
 *
 *   1. Typed name: user fills the text input and clicks "Join".
 *      → onSubmit(trimmedName, 'typed')
 *
 *   2. Guest name: user clicks "Continue as guest".
 *      A candidate preview (generated once, stable) shows the name they'll
 *      appear as.  → onSubmit(candidateName, 'guest')
 *
 * Submitting the form with a blank / whitespace-only input also routes to
 * the guest path (FR-010).
 *
 * Accessibility: the input is auto-focused; the form has a labelled input
 * and an explicit form role. Both action controls have clear ARIA labels.
 */
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generateGuestName } from '@webrtc/protocol'
import styles from './NameGate.module.css'

interface NameGateProps {
  onSubmit: (name: string, source: 'typed' | 'guest') => void
}

export function NameGate({ onSubmit }: NameGateProps) {
  const { t } = useTranslation()
  const [value, setValue] = useState('')

  // Generate the guest candidate once and keep it stable across re-renders.
  // Using a ref ensures it never changes even if the component re-renders.
  const guestCandidate = useRef<string>(generateGuestName(() => false))

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = value.trim()
    if (trimmed) {
      onSubmit(trimmed, 'typed')
    } else {
      // Blank input → guest path (FR-010)
      onSubmit(guestCandidate.current, 'guest')
    }
  }

  function handleGuestClick() {
    onSubmit(guestCandidate.current, 'guest')
  }

  return (
    <div data-testid="name-gate" className={styles.overlay}>
      <div className={styles.card}>
        <h2 className={styles.title}>{t('nameGate.title')}</h2>

        <form onSubmit={handleSubmit} className={styles.form}>
          <label htmlFor="name-gate-input" className={styles.label}>
            {t('nameGate.inputLabel')}
          </label>
          <input
            id="name-gate-input"
            data-testid="name-gate-input"
            type="text"
            className={styles.input}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('nameGate.placeholder')}
            autoFocus
            autoComplete="nickname"
          />
          <button
            type="submit"
            data-testid="name-gate-join"
            className={styles.btnPrimary}
          >
            {t('nameGate.join')}
          </button>
        </form>

        <div className={styles.guestSection}>
          <p data-testid="name-gate-guest-preview" className={styles.guestPreview}>
            {t('nameGate.guestPreview', { name: guestCandidate.current })}
          </p>
          <button
            type="button"
            data-testid="name-gate-guest"
            className={styles.btnSecondary}
            onClick={handleGuestClick}
          >
            {t('nameGate.continueAsGuest')}
          </button>
        </div>
      </div>
    </div>
  )
}
